import {
  activateDrawer,
  afterDrawerClose,
  lockScroll,
  bindDrawerEscape,
  releaseLockedScroll,
  syncModalSwipeOnly,
} from '@theme/coco-utilities';

const DESIGN_MODE_BOUND = { value: false };

const SUCCESS_CLOSE_DELAY = 3000;

const REPEAT_INTERVALS = {
  no_repeat: 0,
  every_30_mins: 30 * 60 * 1000,
  every_1_hr: 60 * 60 * 1000,
  every_6_hrs: 6 * 60 * 60 * 1000,
  every_12_hrs: 12 * 60 * 60 * 1000,
  every_day: 24 * 60 * 60 * 1000,
  every_3_days: 3 * 24 * 60 * 60 * 1000,
  every_week: 7 * 24 * 60 * 60 * 1000,
  every_2_weeks: 14 * 24 * 60 * 60 * 1000,
  every_month: 30 * 24 * 60 * 60 * 1000,
};

function getScrollY() {
  const lenisScroll = window.lenis?.scroll;
  if (Number.isFinite(lenisScroll)) return lenisScroll;

  const smoother = window.ScrollSmoother?.get?.();
  if (smoother) {
    const smootherScroll = smoother.scrollTop();
    if (Number.isFinite(smootherScroll)) return smootherScroll;
  }

  return window.scrollY || document.documentElement.scrollTop || 0;
}

function getNewsletterPopup(id) {
  if (id) return document.getElementById(id);
  return document.querySelector('[is="newsletter-popup"]');
}

function getModalForSection(sectionId) {
  if (!sectionId) return null;
  return document.querySelector(
    `[is="newsletter-popup"][data-section-id="${CSS.escape(String(sectionId))}"]`
  );
}

function removeStaleModals(keep = null) {
  document.querySelectorAll('[is="newsletter-popup"]').forEach((modal) => {
    if (modal === keep) return;
    modal.forceClose?.();
    modal.remove();
  });
}

function bindDesignModeEvents() {
  if (DESIGN_MODE_BOUND.value) return;
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  DESIGN_MODE_BOUND.value = true;

  document.addEventListener('shopify:section:unload', () => {
    removeStaleModals();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    requestAnimationFrame(() => {
      const modal = getModalForSection(sectionId);
      if (!modal) return;

      removeStaleModals(modal);
      modal.show();
    });
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;

    getModalForSection(sectionId)?.forceClose?.();
  });
}

function waitForDocumentReady() {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    window.addEventListener('load', resolve, { once: true });
  });
}

function isAgeVerificationActive() {
  const age = document.querySelector('[is="age-verification"]');
  if (!age) return false;
  return age.hasAttribute('active') || age.hasAttribute('open');
}

function hasAgeVerificationCookie() {
  return /(?:^|;\s*)coco:age-verifier\s*=/.test(document.cookie);
}

function waitForAgeVerificationDismissal() {
  const age = document.querySelector('[is="age-verification"]');
  if (!age) return Promise.resolve();

  if (hasAgeVerificationCookie()) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const cleanup = () => {
      document.removeEventListener('coco:age-verification:dismissed', finish);
      observer.disconnect();
      clearTimeout(waitForOpen);
    };

    document.addEventListener('coco:age-verification:dismissed', finish, { once: true });

    const observer = new MutationObserver(() => {
      if (!isAgeVerificationActive()) finish();
    });
    observer.observe(age, { attributes: true, attributeFilter: ['active', 'open'] });

    // Age verification opens after ~1s; only skip the wait if it never appears.
    const waitForOpen = setTimeout(() => {
      if (!isAgeVerificationActive()) finish();
    }, 2000);
  });
}

class NewsletterPopup extends HTMLDivElement {
  constructor() {
    super();
    this.activeElement = null;
    this.releaseModal = null;
    this.loadTimeout = null;
    this.onScroll = this.onScroll.bind(this);
    this.onLenisScroll = this.onLenisScroll.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.unbindEscape = null;
    this.scrollCleanup = null;
    this.lenisBindTimer = null;
    this.triggerStarted = false;
    this.successCloseTimeout = null;
    this.closeTimeout = null;
    this.deferShowHandler = null;
  }

  isFormSuccess() {
    return Boolean(this.querySelector('[data-newsletter-message]'));
  }

  get isDesignMode() {
    return typeof Shopify !== 'undefined' && Shopify.designMode;
  }

  get testMode() {
    return this.dataset.testMode === 'true';
  }

  get shouldLock() {
    return !this.isDesignMode;
  }

  get storageKey() {
    return `coco:newsletter-popup:${this.dataset.sectionId || this.id}`;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get triggerType() {
    return (this.dataset.trigger || 'delay').trim();
  }

  get triggerDelay() {
    const delay = parseInt(this.dataset.triggerDelay, 10);
    return Number.isFinite(delay) && delay > 0 ? delay : 5;
  }

  get scrollThreshold() {
    const threshold = parseInt(this.dataset.scrollThreshold, 10);
    return Number.isFinite(threshold) && threshold > 0 ? threshold : 400;
  }

  connectedCallback() {
    removeStaleModals(this);
    this.ensureClosed();

    this.addEventListener('click', this.onClick, true);
    this.unbindEscape = bindDrawerEscape(this, () => this.hide());
    this.bindCloseTriggers();
    this.querySelector('[is="newsletter-form"] form, form[data-contact-form]')?.addEventListener(
      'submit',
      this.onFormSubmit
    );
    this.moveToBody();
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick, true);
    this.unbindEscape?.();
    this.unbindEscape = null;
    this.unbindScrollTrigger();
    clearTimeout(this.loadTimeout);
    clearTimeout(this.successCloseTimeout);
    clearTimeout(this.closeTimeout);
    this.querySelector('[is="newsletter-form"] form, form[data-contact-form]')?.removeEventListener(
      'submit',
      this.onFormSubmit
    );
    if (this.deferShowHandler) {
      document.removeEventListener('coco:age-verification:dismissed', this.deferShowHandler);
      this.deferShowHandler = null;
    }
    this.forceClose();
  }

  ensureClosed() {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = null;
    clearTimeout(this.successCloseTimeout);
    this.successCloseTimeout = null;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
    this.unbindScrollTrigger();
    this.removeAttribute('open');
    this.removeAttribute('active');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('show-image');
  }

  async init() {
    bindDesignModeEvents();

    if (this.isDesignMode) {
      if (this.testMode) this.load(0);
      return;
    }

    if (this.testMode) return;

    const formSuccess = this.isFormSuccess();

    if (!formSuccess && (!this.isEligible() || !this.canShow())) return;

    await waitForDocumentReady();
    await waitForAgeVerificationDismissal();
    if (!this.isConnected) return;

    if (formSuccess) {
      this.showSuccessState();
      return;
    }

    this.startTrigger();
  }

  showSuccessState() {
    clearTimeout(this.successCloseTimeout);
    this.show();
    this.successCloseTimeout = setTimeout(() => this.hide(), SUCCESS_CLOSE_DELAY);
  }

  resetTrigger() {
    if (this.isDesignMode) return;
    if (this.testMode) return;
    if (this.hasAttribute('active') || this.hasAttribute('open')) return;

    clearTimeout(this.loadTimeout);
    this.loadTimeout = null;
    this.unbindScrollTrigger();
    this.triggerStarted = false;

    if (!this.isEligible() || !this.canShow()) return;

    this.startTrigger();
  }

  startTrigger() {
    if (this.triggerStarted) return;
    this.triggerStarted = true;
    this.bindTrigger();
  }

  isEligible() {
    if (this.dataset.homeOnly === 'true' && this.dataset.onHomePage !== 'true') return false;
    if (this.dataset.visitorsOnly === 'true' && this.dataset.customerLoggedIn === 'true') return false;
    return true;
  }

  canShow() {
    const repeat = this.dataset.repeat || 'no_repeat';
    const dismissedAt = this.getDismissedAt();

    if (!dismissedAt) return true;
    if (repeat === 'no_repeat') return false;

    const interval = REPEAT_INTERVALS[repeat];
    if (!interval) return true;

    return Date.now() - dismissedAt >= interval;
  }

  getDismissedAt() {
    try {
      const value = localStorage.getItem(this.storageKey);
      return value ? parseInt(value, 10) : null;
    } catch {
      return null;
    }
  }

  setDismissedAt() {
    try {
      localStorage.setItem(this.storageKey, String(Date.now()));
    } catch {
      /* ignore */
    }
  }

  clearDismissedAt() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* ignore */
    }
  }

  bindCloseTriggers() {
    this.querySelectorAll('[is="close-drawer"], [data-fixed-overlay]').forEach((trigger) => {
      trigger.removeEventListener('click', this.onCloseClick);
      trigger.addEventListener('click', this.onCloseClick);
    });
  }

  onCloseClick(event) {
    if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

    event.preventDefault();
    event.stopPropagation();
    this.hide();
  }

  bindScrollTrigger() {
    this.unbindScrollTrigger();

    const cleanup = [];

    window.addEventListener('scroll', this.onScroll, { passive: true });
    cleanup.push(() => window.removeEventListener('scroll', this.onScroll));

    const bindLenis = () => {
      if (!window.lenis?.on) return false;
      window.lenis.on('scroll', this.onLenisScroll);
      cleanup.push(() => window.lenis?.off?.('scroll', this.onLenisScroll));
      return true;
    };

    if (!bindLenis()) {
      this.lenisBindTimer = window.setInterval(() => {
        if (!bindLenis()) return;
        clearInterval(this.lenisBindTimer);
        this.lenisBindTimer = null;
      }, 100);
      cleanup.push(() => {
        if (this.lenisBindTimer) {
          clearInterval(this.lenisBindTimer);
          this.lenisBindTimer = null;
        }
      });
    }

    this.scrollCleanup = () => {
      cleanup.forEach((release) => release());
      this.scrollCleanup = null;
    };
  }

  unbindScrollTrigger() {
    this.scrollCleanup?.();
    this.scrollCleanup = null;
  }

  bindTrigger() {
    const trigger = this.triggerType;

    if (trigger === 'immediate') {
      this.load(0);
      return;
    }

    if (trigger === 'delay') {
      this.load(this.triggerDelay);
      return;
    }

    if (trigger === 'scroll') {
      this.bindScrollTrigger();
    }
  }

  checkScrollThreshold(explicitY) {
    const y = Number.isFinite(explicitY) ? explicitY : getScrollY();
    if (y < this.scrollThreshold) return false;

    this.unbindScrollTrigger();
    this.show();
    return true;
  }

  onLenisScroll(event) {
    this.checkScrollThreshold(event?.scroll);
  }

  onScroll() {
    this.checkScrollThreshold();
  }

  load(delaySeconds) {
    if (this.isDesignMode) return;

    const seconds = Number(delaySeconds);
    const delay = Number.isFinite(seconds) && seconds >= 0 ? seconds : this.triggerDelay;

    clearTimeout(this.loadTimeout);
    this.loadTimeout = setTimeout(() => this.show(), delay * 1000);
  }

  moveToBody() {
    if (this.parentElement === document.body) return;
    document.body.appendChild(this);
  }

  forceClose() {
    clearTimeout(this.loadTimeout);
    this.loadTimeout = null;
    clearTimeout(this.successCloseTimeout);
    this.successCloseTimeout = null;
    clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
    this.unbindScrollTrigger();
    this.triggerStarted = false;

    if (this.releaseModal) {
      this.releaseModal();
      this.releaseModal = null;
    }

    this.removeAttribute('open');
    this.removeAttribute('active');
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('show-image');
    this.activeElement = null;

    releaseLockedScroll(this);
    syncModalSwipeOnly(null);
  }

  onClick(event) {
    if (!this.hasAttribute('active')) return;

    const inner = this.querySelector('[data-drawer-inner]');
    const isInsidePanel = inner?.contains(event.target);

    if (
      event.target.closest('[is="close-drawer"]') ||
      event.target.closest('.fixed-overlay')
    ) {
      event.preventDefault();
      this.hide();
      return;
    }

    if (!isInsidePanel) {
      event.preventDefault();
      this.hide();
    }
  }

  onFormSubmit() {
    if (!this.testMode) this.setDismissedAt();
  }

  show(triggeredBy) {
    if (!this.isDesignMode && isAgeVerificationActive()) {
      if (!this.deferShowHandler) {
        const pendingTrigger = triggeredBy;
        this.deferShowHandler = () => {
          this.deferShowHandler = null;
          this.show(pendingTrigger);
        };
        document.addEventListener('coco:age-verification:dismissed', this.deferShowHandler, {
          once: true,
        });
      }
      return;
    }

    if (triggeredBy) this.activeElement = triggeredBy;

    this.removeAttribute('inert');

    this.unbindScrollTrigger();
    clearTimeout(this.loadTimeout);

    this.releaseModal?.();
    this.releaseModal = null;

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('open', '');
    requestAnimationFrame(() => this.setAttribute('active', ''));
    if (this.shouldLock) {
      lockScroll(this);
      this.setAttribute('data-scroll-locked', '');
    } else {
      // Theme editor skips scroll-lock, but still needs swipe-only chrome (hide X).
      syncModalSwipeOnly(this);
    }

    if (triggeredBy) triggeredBy.setAttribute('aria-expanded', 'true');

    if (!this.isDesignMode) {
      this.releaseModal = activateDrawer(this, {
        initialFocus: (drawer) =>
          drawer.querySelector('[is="close-drawer"]:not([data-fixed-overlay])') ||
          drawer.querySelector('input[type="email"]') ||
          drawer,
        trap: true,
      });
    }

    this.afterShow();
  }

  hide() {
    if (!this.hasAttribute('active') && !this.hasAttribute('open')) return;

    clearTimeout(this.successCloseTimeout);
    this.successCloseTimeout = null;

    this.classList.remove('show-image');
    this.removeAttribute('active');

    const trigger =
      this.activeElement?.isConnected
        ? this.activeElement
        : document.querySelector(`[data-newsletter-open][aria-controls="${this.id}"]`);
    trigger?.setAttribute?.('aria-expanded', 'false');

    const releaseModal = this.releaseModal;
    this.releaseModal = null;
    this.activeElement = null;
    releaseModal?.(trigger);

    const finish = () => {
      if (this.hasAttribute('active')) return;

      this.removeAttribute('open');
      this.setAttribute('hidden', '');
      this.setAttribute('aria-hidden', 'true');
      releaseLockedScroll(this);
      this.afterHide();
    };

    if (this.isDesignMode) {
      finish();
      return;
    }

    clearTimeout(this.closeTimeout);
    afterDrawerClose(this, () => {
      this.closeTimeout = null;
      finish();
    });
  }

  afterShow() {
    this.classList.add('show-image');

    if (this.isFormSuccess()) {
      this.querySelector('[data-newsletter-message]')?.focus?.();
    }
  }

  afterHide() {
    if (this.isDesignMode) return;

    if (this.testMode) {
      this.clearDismissedAt();
      return;
    }

    this.setDismissedAt();
  }
}

function handleDocumentClick(event) {
  const trigger = event.target.closest('[data-newsletter-open]');
  if (!trigger) return;

  const popupId = trigger.getAttribute('aria-controls');
  const popup = getNewsletterPopup(popupId);
  if (!popup) return;

  event.preventDefault();
  popup.show(trigger);
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup, { extends: 'div' });
  document.addEventListener('click', handleDocumentClick);
}

document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail?.sectionId;
  if (!sectionId) return;

  requestAnimationFrame(() => {
    getModalForSection(sectionId)?.resetTrigger?.();
  });
});
